import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  FiArrowLeft,
  FiSave,
  FiEye,
  FiEyeOff,
  FiToggleRight,
  FiInfo,
  FiCheckCircle,
  FiAlertCircle
} from 'react-icons/fi';
import { GlassCard } from '../../components/ui/GlassCard';
import {
  fetchPaymentMethods,
  updatePaymentMethod,
  togglePaymentMethod
} from '../../store/slices/adminPaymentSlice';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

export const AdminPaymentConfigPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { paymentMethods, isLoading } = useSelector((state) => state.adminPayment);
  const [editingMethod, setEditingMethod] = useState(null);
  const [formData, setFormData] = useState({
    apiKey: '',
    apiSecret: '',
    baseUrl: ''
  });
  const [showSecrets, setShowSecrets] = useState({});

  useEffect(() => {
    dispatch(fetchPaymentMethods());
  }, [dispatch]);

  const handleEditClick = (method) => {
    setEditingMethod(method.name);
    setFormData({
      apiKey: '',
      apiSecret: '',
      baseUrl: method.baseUrl || ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!formData.apiKey || !formData.apiSecret) {
      toast.error('Please provide API Key and API Secret');
      return;
    }

    await dispatch(updatePaymentMethod({
      methodName: editingMethod,
      data: {
        apiKey: formData.apiKey,
        apiSecret: formData.apiSecret,
        baseUrl: formData.baseUrl
      }
    }));

    setEditingMethod(null);
  };

  const handleToggle = async (methodName) => {
    await dispatch(togglePaymentMethod(methodName));
  };

  const getMethodIcon = (methodName) => {
    switch(methodName) {
      case 'korapay':
        return '🟠';
      case 'paystack':
        return '💳';
      case 'wallet':
        return '💰';
      default:
        return '💵';
    }
  };

  const getMethodDescription = (methodName) => {
    switch(methodName) {
      case 'korapay':
        return 'Korapay payment gateway for deposits';
      case 'paystack':
        return 'Paystack payment gateway for deposits';
      case 'wallet':
        return 'Wallet balance for purchases';
      default:
        return '';
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  return (
    <>
      <Helmet>
        <title>Payment Configuration | Admin Dashboard</title>
      </Helmet>

      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-luxury-50 via-white to-luxury-100">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-6"
          >
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="flex items-center space-x-2 text-luxury-600 hover:text-primary-600 transition-colors"
            >
              <FiArrowLeft className="w-5 h-5" />
              <span>Back to Dashboard</span>
            </button>
          </motion.div>

          {/* Header */}
          <motion.div variants={fadeInUp} initial="initial" animate="animate" className="mb-8">
            <h1 className="text-3xl font-display font-bold text-luxury-900 mb-2">
              Payment Configuration
            </h1>
            <p className="text-luxury-600">
              Manage payment methods and configure API credentials
            </p>
          </motion.div>

          {/* Info Card */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="mb-8 glass rounded-2xl p-6 border-l-4 border-blue-500"
          >
            <div className="flex items-start space-x-3">
              <FiInfo className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-luxury-900 mb-1">Configuration Guide</h3>
                <p className="text-sm text-luxury-600">
                  Enable or disable payment methods for your users. Configure API credentials for payment gateways.
                  Users will only see enabled payment methods at checkout.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Payment Methods Grid */}
          <div className="grid gap-6">
            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin inline-block w-8 h-8 border-4 border-primary-300 border-t-primary-600 rounded-full"></div>
                <p className="text-luxury-600 mt-4">Loading payment methods...</p>
              </div>
            ) : paymentMethods.length === 0 ? (
              <GlassCard className="p-12 text-center">
                <p className="text-luxury-600">No payment methods found</p>
              </GlassCard>
            ) : (
              paymentMethods.map((method, index) => (
                <motion.div
                  key={method._id}
                  variants={fadeInUp}
                  initial="initial"
                  animate="animate"
                  transition={{ delay: index * 0.1 }}
                >
                  <GlassCard className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start space-x-4">
                        {/* <div className="text-4xl">{getMethodIcon(method.name)}</div> */}
                        <img src={method?.logo} alt={method.name} className="w-16 h-16 object-contain" />
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-xl font-semibold text-luxury-900 capitalize">
                              {method.name}
                            </h3>
                            {method.enabled ? (
                              <span className="inline-flex items-center space-x-1 bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm">
                                <FiCheckCircle className="w-4 h-4" />
                                <span>Enabled</span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center space-x-1 bg-gray-50 text-gray-700 px-3 py-1 rounded-full text-sm">
                                <FiAlertCircle className="w-4 h-4" />
                                <span>Disabled</span>
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-luxury-600">
                            {getMethodDescription(method.name)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Status and Config Info */}
                    <div className="mb-6 p-4 bg-luxury-50 rounded-lg">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-luxury-500 mb-1">Status</p>
                          <p className="text-sm font-medium">
                            {method.enabled ? '✅ Active' : '❌ Inactive'}
                          </p>
                        </div>
                        {method.hasApiKey && (
                          <div>
                            <p className="text-xs text-luxury-500 mb-1">API Key</p>
                            <p className="text-sm font-medium">✓ Configured</p>
                          </div>
                        )}
                        {method.hasApiSecret && (
                          <div>
                            <p className="text-xs text-luxury-500 mb-1">API Secret</p>
                            <p className="text-sm font-medium">✓ Configured</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Edit Form */}
                    {editingMethod === method.name && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mb-6 p-4 bg-primary-50 rounded-lg border border-primary-200"
                      >
                        <h4 className="font-semibold text-luxury-900 mb-4">Configure {method.name.toUpperCase()}</h4>

                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-luxury-900 mb-2">
                              API Key
                            </label>
                            <small>this is your public key from the {method.name} dashboard</small>
                            <input
                              type="text"
                              name="apiKey"
                              placeholder="Enter API Key"
                              value={formData.apiKey}
                              onChange={handleInputChange}
                              className="w-full px-4 py-2 border border-luxury-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-luxury-900 mb-2">
                              API Secret
                            </label>
                            <div className="relative">
                              <input
                                type={showSecrets[method.name] ? 'text' : 'password'}
                                name="apiSecret"
                                placeholder="Enter API Secret"
                                value={formData.apiSecret}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-luxury-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                              />
                              <button
                                type="button"
                                onClick={() => setShowSecrets(prev => ({
                                  ...prev,
                                  [method.name]: !prev[method.name]
                                }))}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-luxury-600 hover:text-luxury-900"
                              >
                                {showSecrets[method.name] ? (
                                  <FiEyeOff className="w-5 h-5" />
                                ) : (
                                  <FiEye className="w-5 h-5" />
                                )}
                              </button>
                            </div>
                          </div>

                          {method.name === 'korapay' && (
                            <div>
                              <label className="block text-sm font-medium text-luxury-900 mb-2">
                                Base URL (Optional)
                              </label>
                              <input
                                type="text"
                                name="baseUrl"
                                placeholder="e.g., https://api.korapay.com/merchant/api/v1"
                                value={formData.baseUrl}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-luxury-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                              />
                              <p className="text-xs text-luxury-500 mt-1">
                                Leave empty to use default Korapay API endpoint
                              </p>
                            </div>
                          )}

                          <div className="flex justify-end space-x-3 pt-4">
                            <button
                              onClick={() => setEditingMethod(null)}
                              className="px-4 py-2 border border-luxury-200 rounded-lg text-luxury-700 hover:bg-luxury-50 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleSave}
                              disabled={isLoading}
                              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
                            >
                              <FiSave className="w-4 h-4" />
                              <span>Save Configuration</span>
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-between items-center pt-4 border-t border-luxury-200">
                      <div className="flex space-x-2">
                        {method.name !== 'wallet' && (
                          <button
                            onClick={() => handleEditClick(method)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                          >
                            Configure
                          </button>
                        )}
                      </div>
                      <button
                        onClick={() => handleToggle(method.name)}
                        disabled={isLoading}
                        className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 text-sm font-medium ${
                          method.enabled
                            ? 'bg-red-50 text-red-700 hover:bg-red-100'
                            : 'bg-green-50 text-green-700 hover:bg-green-100'
                        } disabled:opacity-50`}
                      >
                        <FiToggleRight className="w-4 h-4" />
                        <span>{method.enabled ? 'Disable' : 'Enable'}</span>
                      </button>
                    </div>
                  </GlassCard>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
};
